// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Button, Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { downloadFileThunk } from '../downloadFileThunk';
import stylesDef from './styles';

interface ComponentProps {
  // Downloadable files in the pane's displayed order; the thunk serves every
  // kind by basename.
  files: IDownloadableFileRef[];
  // 'end' right-aligns the cluster so stacked section headers share one pill
  // edge (D83); 'center' suits a standalone row.
  align: 'center' | 'end';
  // 'N selected' in place of 'N of M selected', for headers short on room.
  compact?: boolean;
}

// The shared bulk-download row (caption + pill): a selection narrows the
// download to that subset, no selection means all. The 3s stagger keeps large
// sets inside browser download limits (a single zip replaces this under
// AUT-5131).
export const BulkDownloadRow: React.FC<ComponentProps> = ({ files, align, compact }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state) => state.fileSelection.selected);

  const selectedFiles = files.filter((file) => selected[file.kind].includes(file.id));
  const hasSelection = selectedFiles.length > 0;
  const selectionLabel = compact
    ? `${selectedFiles.length} selected`
    : `${selectedFiles.length} of ${files.length} selected`;

  const handleDownload = async () => {
    const toDownload = hasSelection ? selectedFiles : files;
    const interval = 3000;
    toDownload.forEach((file, index) => {
      setTimeout(async () => {
        await dispatch(downloadFileThunk(file));
      }, index * interval);
    });
  };

  if (!files.length) return null;

  return (
    <Box sx={[styles.row, align === 'end' && styles.rowEnd]}>
      <Typography variant="caption" sx={styles.count}>
        {hasSelection ? selectionLabel : `${files.length} ${files.length === 1 ? 'file' : 'files'}`}
      </Typography>
      <Button
        variant={hasSelection ? 'contained' : 'outlined'}
        size="small"
        disableElevation
        aria-label={hasSelection ? 'Download selected files' : 'Download all files'}
        sx={styles.downloadButton}
        onClick={() => handleDownload()}
      >
        {hasSelection ? 'Download' : 'Download All'}
      </Button>
    </Box>
  );
};

BulkDownloadRow.defaultProps = {
  compact: false,
};

export default BulkDownloadRow;
